import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Table, Tooltip } from "antd";
import axios from "axios";
import { TableCustomProps } from "../../../../lib/interfaces/TableRecord";
import { ReactComponent as PaymentDisconfirmIcon } from "../../../../img/payment-disconfirm-icon.svg";
import { ReactComponent as PrepaidBillIcon } from "../../../../img/payment_icons/prepaid-bill.svg";
import { ReactComponent as PostpaidBillIcon } from "../../../../img/payment_icons/postpaid-bill.svg";
import { ReactComponent as BillFactureIcon } from "../../../../img/payment_icons/bill-facture.svg";
import { ReactComponent as InvoiceIcon } from "../../../../img/payment_icons/invoice.svg";
import { ReactComponent as ChangeSumIcon } from "../../../../img/change-sum-icon.svg";
import { ReactComponent as ServicesIcon } from "../../../../img/sidemenu-icons/services-icon.svg";
import { Payment } from "../../../../lib/types/Payment";
import { checkIsLoading, round, toDisplayFormat } from "../../../../lib/utils";
import { useAppSelector } from "../../../..";
import ToggleColumns from "../../../../components/ToggleableVersionsTable/ToggleColumns";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { MENU_ACCOUNT_PAYMENTS_WRITE } from "../../../../lib/const/permissions";
import { useActiveModal } from "../../../../lib/utils/useActiveModal";
import { Offer } from "../../../../lib/types";
import buildUrl from "../../../../lib/utils/buildUrl";
import { baseUrl } from "../../../../lib/routes/baseUrl";
import { DataTooltip } from "../../../../components/common/DataTooltip/DataTooltip";
import "./AccountPaymentGoals.less";

interface TableRecord {
  goalId: string | undefined;
  id: number | undefined;
  goalInvoiceExtNum?: string;
  goalTypeId: number;
  amount: string;
  priority: number;
  period: string;
  billAmount: string | undefined;
  billAmountLeft: string | undefined;
  billId: number | undefined;
  goalOffer: Offer | undefined;
}

type TableProps = TableCustomProps<TableRecord>;

const columnWidths: TableProps["columnWidths"] = {};

interface ComponentProps {
  goals: Payment["goals"] | undefined;
  isLoading: boolean;
  uncoverGoal: (id: number | string) => Promise<void>;
}

export const AccountPaymentGoals = (props: ComponentProps) => {
  const { goals, isLoading, uncoverGoal } = props;

  const { activeModal, setActiveModal, activeModalOptions } = useActiveModal<
    "" | "uncoverGoal" | "freeUpUnspentSum"
  >("");

  const closeActiveModal = () => {
    setActiveModal("");
  };

  const handleUncoverGoal = () => {
    const id = (activeModalOptions as TableRecord)?.id;
    if (id) {
      uncoverGoal(id).finally(closeActiveModal);
    }
  };

  const { paymentLoading } = useAppSelector((state) => state.accountPaymentDetails);

  const getGoalIcon = (goalTypeId) => {
    const icons = {
      1: [<PrepaidBillIcon key={1} />, "Счет на предоплату"],
      2: [<PostpaidBillIcon key={1} />, "Счет на постоплату"],
      3: [<BillFactureIcon key={1} />, "Счет-фактура"],
      4: [<InvoiceIcon key={1} />, "Инвойс"],
      5: [<ServicesIcon key={1} color="#00BBFF" />, "Предложение"],
      6: [<ServicesIcon key={1} color="#0057FF" />, "Продукт"],
    };
    const [icon, title] = icons[goalTypeId];
    return (
      <div style={{ marginRight: "5px", width: "24px", height: "24px" }}>
        <Tooltip title={title}>{icon}</Tooltip>
      </div>
    );
  };

  // Список офферов из целей для отображения name и extName в тултипе
  const [goalsOfferList, setGoalsOfferList] = useState<Offer[]>();

  useEffect(() => {
    const offerIds =
      goals?.filter(({ goalTypeId }) => goalTypeId === 5).map(({ goalId }) => goalId) || [];

    const getUrl = (id: string) =>
      buildUrl({
        url: [baseUrl, "offerings-api", "offering", id].join("/"),
        searchParams: {
          extendedInfo: false,
          withChars: false,
        },
      });
    Promise.all(offerIds.map((id) => axios.get<Offer>(getUrl(id)))).then((rslt) => {
      const list = rslt.map(({ data }) => data);
      setGoalsOfferList(list);
    });
  }, [goals]);

  const getColumns = (): TableProps["columns"] => [
    {
      title: "Цель платежа",
      dataIndex: "goalId",
      key: "goalId",
      width: columnWidths.goalId,
      render: (goalId, { goalInvoiceExtNum, goalTypeId, goalOffer }) => (
        <div className="account-payment-goals__goalId">
          {getGoalIcon(goalTypeId)}
          <div>
            {/*	Для Типа цели goalTypeId = 3 (счет-фактура) вместо goalId выводим goals: invoices: extNum
                  Если в массиве invoices несколько элементов, то берем из первого. */}
            {[1, 2, 4, 6].includes(goalTypeId) && goalId}
            {goalTypeId === 3 && goalInvoiceExtNum}
            {goalTypeId === 5 && goalOffer && (
              <DataTooltip
                dataObject={goalOffer}
                includedFields={["name", "extName"]}
                placement="topLeft"
              >
                {goalId}
              </DataTooltip>
            )}
            {goalTypeId === 4 && <div>{goalInvoiceExtNum}</div>}
          </div>
        </div>
      ),
    },
    {
      title: "Сумма цели",
      dataIndex: "amount",
      key: "amount",
      width: columnWidths.amount,
    },
    {
      title: "Приоритет",
      dataIndex: "priority",
      key: "priority",
      width: columnWidths.priority,
      hidden: true,
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnWidths.period,
      hidden: true,
    },
    {
      title: "Сумма счёта",
      dataIndex: "billAmount",
      key: "billAmount",
      width: columnWidths.billAmount,
      hidden: true,
    },
    {
      title: "Остаток к оплате по счету",
      dataIndex: "billAmountLeft",
      key: "billAmountLeft",
      width: columnWidths.billAmountLeft,
      hidden: true,
    },
    {
      key: "actions",
      width: columnWidths.actions,
      render: (_, goal) => {
        return (
          <div className="account-payment-goals__btns">
            {[1, 2, 5, 6].includes(goal.goalTypeId) && hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE) && (
              <Tooltip title="Снять отметку оплаты">
                <PaymentDisconfirmIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveModal("uncoverGoal", goal);
                  }}
                />
              </Tooltip>
            )}
            {goal.priority === 0 && hasAccess(MENU_ACCOUNT_PAYMENTS_WRITE) && (
              <Tooltip title="Освободить неизрасходованные средства">
                <ChangeSumIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveModal("freeUpUnspentSum", goal);
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const [columns, setColumns] = useState(getColumns());

  const tableData = useMemo(() => {
    const data: TableProps["dataSource"] = goals?.map((goal) => {
      const periodFrom = goal?.bill?.periodFrom ? toDisplayFormat(goal.bill.periodFrom) : "";
      const periodTo = goal?.bill?.periodTo ? toDisplayFormat(goal?.bill?.periodTo) : "";
      const goalOffer =
        goal.goalTypeId === 5
          ? goalsOfferList?.find(({ id }) => String(id) === goal.goalId)
          : undefined;
      return {
        key: goal.goalId,
        id: goal.id,
        goalTypeId: goal.goalTypeId,
        goalId: goal.goalId,
        goalInvoiceExtNum: goal.invoices?.[0]?.extNum,
        amount: round(goal.amount),
        priority: goal.priority,
        period: `${periodFrom} - ${periodTo}`,
        billAmount: round(goal.bill?.amount),
        billAmountLeft: round(goal.bill?.amountLeft),
        billId: goal.bill?.id,
        goalOffer,
      };
    });
    return data;
  }, [goals, goalsOfferList]);

  return (
    <>
      <div className="account-payment-goals">
        <Table
          title={() => (
            <ToggleColumns
              className="account-payment-goals__columnsToggler"
              tableName="account-payment-goals"
              columns={columns}
              setColumns={setColumns}
            />
          )}
          size="small"
          columns={[...columns].filter((item) => !item.hidden)}
          dataSource={tableData}
          loading={checkIsLoading(paymentLoading)}
          pagination={false}
          scroll={{ y: "250px" }}
        />
      </div>
      <Modal
        className="payment-disconfirm-modal"
        visible={activeModal === "uncoverGoal"}
        onCancel={closeActiveModal}
        footer={
          <>
            <Button onClick={closeActiveModal}>Отмена</Button>
            <Button type="primary" onClick={handleUncoverGoal} loading={isLoading}>
              Далее
            </Button>
          </>
        }
      >
        <div>Вы действительно хотите снять отметку оплаты?</div>
      </Modal>
      <Modal
        className="payment-freeUpUnspentSum-modal"
        visible={activeModal === "freeUpUnspentSum"}
        onCancel={closeActiveModal}
        footer={
          <>
            <Button onClick={closeActiveModal}>Отмена</Button>
            <Tooltip title="В разработке">
              <Button type="primary" onClick={() => {}} loading={isLoading} disabled>
                Сохранить
              </Button>
            </Tooltip>
          </>
        }
      >
        <div>Вы хотите присвоить сумму цели согласно текущей сумме</div>
        <div>покрытия инвойсов?</div>
      </Modal>
    </>
  );
};
